import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Key, ArrowRight, AlertCircle, Building } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreJoiningLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", access_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/prejoining/login`, {
        email: formData.email.toLowerCase().trim(),
        access_code: formData.access_code.trim().toUpperCase()
      });

      // Store candidate token
      localStorage.setItem("candidateToken", response.data.access_token);
      localStorage.setItem("candidateInfo", JSON.stringify(response.data.candidate));
      
      toast.success(`Welcome, ${response.data.candidate.name}!`);
      navigate("/prejoining/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed. Please check your credentials.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="prejoining-login-page">
      {/* Header */}
      <nav className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <div 
            className="flex items-center gap-3 cursor-pointer w-fit"
            onClick={() => navigate("/")}
          >
            <div className="w-10 h-10 rounded bg-navy flex items-center justify-center">
              <span className="font-playfair font-bold text-white text-lg">CI</span>
            </div>
            <div className="flex flex-col">
              <span className="font-playfair font-bold text-navy text-lg leading-tight">Creative</span>
              <span className="font-dm text-xs text-gold tracking-wider">INFOTECH</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-navy to-navy/90 p-8 text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-gold" />
              </div>
              <h1 className="font-playfair text-2xl text-white mb-2">Pre-Joining Portal</h1>
              <p className="font-dm text-white/70 text-sm">
                Access your onboarding documents and policies
              </p>
            </div>

            {/* Form */}
            <div className="p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="font-dm text-sm text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block font-dm text-sm text-charcoal mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg font-dm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
                      required
                      data-testid="prejoining-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-dm text-sm text-charcoal mb-2">
                    Access Code
                  </label>
                  <div className="relative">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.access_code}
                      onChange={(e) => setFormData({ ...formData, access_code: e.target.value.toUpperCase() })}
                      placeholder="Enter your unique code"
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg font-dm text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all uppercase tracking-widest"
                      maxLength={8}
                      required
                      data-testid="prejoining-code-input"
                    />
                  </div>
                  <p className="font-dm text-xs text-gray-500 mt-2">
                    The access code was shared by HR via email
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-gold/90 text-white font-dm font-medium py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="prejoining-login-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Access Portal
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="font-dm text-sm text-gray-500 text-center">
                  Need help?{" "}
                  <a href="mailto:info@creativeinfotech.net" className="text-gold hover:underline">
                    Contact HR
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <p className="text-center font-dm text-xs text-gray-500 mt-6">
            This portal is exclusively for candidates who have received an offer from Creative Infotech
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PreJoiningLogin;
